import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Shield } from "lucide-react";
import { FaTrash, FaEdit } from "react-icons/fa";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import toast from "react-hot-toast";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterFormContainer, 
  masterFormSectionHeader, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterFormActionBlock, 
  masterTableCellstatus,
  masterTableDatastatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const AdminManagement = () => {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]); 
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  // Search & Pagination states matching Master standard
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const [newUser, setNewUser] = useState({
    userId: "",
    employeeID: "",
    employeeUserId: "",
    name: "",
    password: "",
    role: "HR",
    permissions: [],
  });

  const permissionsList = ["Dashboard_View", "Master_View", "Reminder_View", "Employee_View", "Leave_Management_View", "Shift_Management_view", "Attendance_history_view", "Pay_slip_view", "Admin_Management_view"];
  const token = localStorage.getItem("token");

  const isMainAdmin = (u) => {
    const id = String(u?.userId || "").trim().toLowerCase();
    return id === "sixfox";
  };

  const fetchData = async () => {
    if (!token) return;
    try {
      const [userRes, empRes] = await Promise.all([
        axios.get("/api/adminManagement/users", { headers: { Authorization: `Bearer ${token}` } }),
        axios.get("/api/employees", { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(userRes.data);
      setEmployees(empRes.data);
    } catch (err) { toast.error(err.message); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAutoFill = (empId) => {
    if (!empId) return;
    const emp = employees.find((e) => e.employeeID?.toUpperCase() === empId.toUpperCase());
    if (emp) {
      const fullName = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ");
      setNewUser((prev) => ({ 
        ...prev, 
        name: fullName, 
        userId: emp.employeeUserId || "",
        employeeUserId: emp.employeeUserId || "",
        employeeID: emp.employeeID || ""
      }));
      toast.success("Details auto-filled!");
    } else { toast.error("Employee ID not found!"); }
  };

  const saveUser = async () => {
    if (!token) return toast.error("Admin session expired!");
    const isEditing = !!editingUserId;
    if (!newUser.userId || !newUser.name || (!isEditing && !newUser.password)) {
      return toast.error("Please fill all required fields");
    }

    try {
      if (isEditing) {
        await axios.put(`/api/adminManagement/users/${editingUserId}`, newUser, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("User updated!");
      } else {
        await axios.post("/api/adminManagement/users", newUser, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("User created!");
      }
      fetchData();
      setEditingUserId(null);
      setNewUser({ userId: "", employeeID: "", employeeUserId: "", name: "", password: "", role: "HR", permissions: [] });
    } catch (err) { toast.error(err.response?.data?.message || "Error saving user"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`/api/adminManagement/users/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted!");
      fetchData();
    } catch (err) { toast.error("Error deleting"); }
  };

  const togglePermission = (perm) => {
    setNewUser((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm) ? prev.permissions.filter((p) => p !== perm) : [...prev.permissions, perm],
    }));
  };

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return users;
    return users.filter((item) => {
      const name = (item.name || '').toLowerCase();
      const userId = (item.userId || '').toLowerCase();
      const empId = (item.employeeID || '').toLowerCase();
      return name.includes(q) || userId.includes(q) || empId.includes(q);
    });
  }, [users, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Shield size={14} /></div>
              <div><h1 className={masterTitle}>Admin Management</h1></div>
            </div>
           
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full overflow-y-auto`}>
          <div className="p-2 space-y-4">
            
            {/* Tips banner */}
            <div className="bg-white border-l-8 border-blue-600 p-3 rounded-lg shadow-sm text-xs md:text-sm">
              <p className="font-bold text-blue-800 mb-1">💡 Admin Tip: Assign specific module permissions carefully.</p>
              <p className="font-bold text-red-600 animate-pulse">💡 Quick Action: Simply enter the Employee ID and press "Enter" to auto-fill details. You only need to provide the password and choose the role.</p>
            </div>

            {/* Form Panel */}
            <div className={masterFormContainer}>
              <div className={masterFormSectionHeader}>
                <h2>{editingUserId ? "Edit Admin User Details" : "Create New Admin User"}</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Employee ID Search</label>
                  <input
                    placeholder="e.g. P00001"
                    className={`${masterInput} uppercase font-bold`}
                    onChange={(e) => {
                      let val = e.target.value.toUpperCase();
                      let clean = val.replace(/-/g, "");
                      let formatted = clean.replace(/^([A-Z]+)(\d+)/, "$1-$2");
                      setNewUser({ ...newUser, employeeID: formatted });
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAutoFill(newUser.employeeID); }}
                    value={newUser.employeeID}
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>User ID</label>
                  <input value={newUser.userId} readOnly className={`${masterInput} bg-slate-100 font-bold cursor-not-allowed`} />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Name</label>
                  <input value={newUser.name} readOnly className={`${masterInput} bg-slate-100 font-bold cursor-not-allowed`} />
                </div>

                <div className={`${masterFormGridRow} relative`}>
                  <label className={masterInputLabel}>Password</label>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={newUser.password} 
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})} 
                    className={`${masterInput} pr-10`} 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-7 text-slate-500">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Role</label>
                  <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className={`${masterSelect} font-semibold`}>
                    <option value="HR">HR</option>
                    <option value="Manager">Manager</option>
                    <option value="Employee">Employee</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-bold text-blue-900 mb-2 text-xs uppercase">Module Permissions:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 font-semibold">
                  {permissionsList.map((perm) => (
                    <label key={perm} className="flex items-center p-2 bg-slate-50 rounded border border-slate-200 cursor-pointer hover:bg-blue-50 transition-colors">
                      <input type="checkbox" checked={newUser.permissions.includes(perm)} onChange={() => togglePermission(perm)} className="mr-2" />
                      <span className="text-[10px] text-slate-700">{perm.replace(/_/g, " ")}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className={masterFormActionBlock}>
                <button onClick={saveUser} className={masterAccentButton}>
                  {editingUserId ? "UPDATE USER" : "CREATE USER"}
                </button>
              </div>
            </div>

            {/* Registered Users Table Panel */}
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-2 sm:p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by name, user ID, or employee ID..." 
                  totalItems={filteredData.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} style={{ width: '60px' }}>S.No</th>
                      <th className={masterTableData}>User ID</th>
                      <th className={masterTableData}>Employee ID</th>
                      <th className={masterTableData}>Name</th>
                      <th className={masterTableData}>Role</th>
                      <th className={masterTableData}>Permissions</th>
                      <th className={masterTableDatastatus}>Action</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((u, index) => {
                        const disabled = isMainAdmin(u);
                        return (
                          <tr key={u._id} className={masterTableRow}>
                            <td className={masterTableCell}>
                              {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                            </td>
                            <td className={`${masterTableCell} font-bold text-blue-900`}>{u.userId}</td>
                            <td className={`${masterTableCell} font-bold`}>{u.employeeID}</td>
                            <td className={masterTableCell}>{u.name}</td>
                            <td className={`${masterTableCell} font-semibold text-orange-600`}>{u.role}</td>
                            <td className={`${masterTableCell} max-w-[300px]`}>
                              <div className="flex flex-wrap justify-center gap-1">
                                {u.permissions?.length > 0 ? u.permissions.map((p, i) => (
                                  <span key={i} className="bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded text-[9px] border border-blue-200">
                                    {p.replace(/_View|_view/g, "")}
                                  </span>
                                )) : <span className="text-gray-400 italic">No Permissions</span>}
                              </div>
                            </td>
                            <td className={masterTableCellstatus}>
                              <div className="flex justify-center items-center gap-1.5">
                                <button 
                                  type="button" 
                                  onClick={() => !disabled && (setEditingUserId(u._id) || setNewUser(u) || window.scrollTo({ top: 0, behavior: "smooth" }))} 
                                  disabled={disabled} 
                                  className={disabled ? "opacity-30 cursor-not-allowed text-slate-400 p-1" : "p-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all"}
                                  title="Edit"
                                >
                                  <FaEdit size={12} />
                                </button>
                                <button 
                                  type="button" 
                                  onClick={() => !disabled && deleteUser(u._id)} 
                                  disabled={disabled} 
                                  className={disabled ? "opacity-30 cursor-not-allowed text-slate-400 p-1" : "p-1 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-all"}
                                  title="Delete"
                                >
                                  <FaTrash size={12} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminManagement;